import React, { useState } from 'react';
import { MoveDownRight } from 'lucide-react';

const servicesData = [
  {
    id: '01',
    title: 'Identity & Compliance Intelligence',
    flagship: 'The CRI Programme',
    description:
      'Authorisation infrastructure designed to formalise undocumented activity into verifiable, accountable data — engineered for sovereign-scale deployment.',
  },
  {
    id: '02',
    title: 'Green Compliance & Circular Economy',
    description:
      'Production architecture that converts environmental compliance obligations into long-duration, scalable revenue streams.',
  },
  {
    id: '03',
    title: 'Sovereign RegTech Solutions (B2G)',
    description:
      'Systems that unblock regulatory deadlocks so foreign capital can enter African markets responsibly and at speed.',
  },
];

const ServiceGraphic = ({ type }) => {
  if (type === 1) {
    // Complex orbital pattern
    return (
      <svg viewBox="0 0 400 400" className="w-full h-full">
        {/* Multiple orbital circles */}
        <circle cx="200" cy="200" r="140" fill="none" stroke="#3eb5a0" strokeWidth="1.5" opacity="0.8" />
        <circle cx="200" cy="200" r="120" fill="none" stroke="#3eb5a0" strokeWidth="1.5" opacity="0.6" />
        <circle cx="200" cy="200" r="100" fill="none" stroke="#3eb5a0" strokeWidth="1.5" opacity="0.5" />
        
        {/* Dotted orbital paths */}
        <circle cx="200" cy="200" r="160" fill="none" stroke="#3eb5a0" strokeWidth="1" opacity="0.4" strokeDasharray="4,4" />
        <circle cx="200" cy="200" r="80" fill="none" stroke="#3eb5a0" strokeWidth="1" opacity="0.3" strokeDasharray="4,4" />
        
        {/* White dots on orbits */}
        <circle cx="200" cy="60" r="5" fill="white" />
        <circle cx="340" cy="200" r="5" fill="white" />
        <circle cx="280" cy="120" r="4" fill="white" />
      </svg>
    );
  } else if (type === 2) {
    // Circle with square overlay
    return (
      <svg viewBox="0 0 400 400" className="w-full h-full">
        {/* Large circles */}
        <circle cx="250" cy="200" r="130" fill="none" stroke="#3eb5a0" strokeWidth="1.5" opacity="0.7" />
        <circle cx="250" cy="200" r="100" fill="none" stroke="#3eb5a0" strokeWidth="1.5" opacity="0.5" />
        
        {/* Overlapping square */}
        <rect x="120" y="220" width="140" height="140" fill="none" stroke="#3eb5a0" strokeWidth="1.5" opacity="0.6" />
        
        {/* Dotted connecting lines */}
        <line x1="200" y1="200" x2="280" y2="120" stroke="#3eb5a0" strokeWidth="1" opacity="0.4" strokeDasharray="4,4" />
        <line x1="190" y1="290" x2="250" y2="200" stroke="#3eb5a0" strokeWidth="1" opacity="0.4" strokeDasharray="4,4" />
        
        {/* White dots */}
        <circle cx="280" cy="120" r="5" fill="white" />
        <circle cx="190" cy="290" r="5" fill="white" />
      </svg>
    );
  } else {
    // Simple large circle with dots
    return (
      <svg viewBox="0 0 400 400" className="w-full h-full">
        {/* Main circles */}
        <circle cx="200" cy="200" r="140" fill="none" stroke="#3eb5a0" strokeWidth="1.5" opacity="0.8" />
        <circle cx="200" cy="200" r="110" fill="none" stroke="#3eb5a0" strokeWidth="1.5" opacity="0.6" />
        
        {/* Dotted path */}
        <circle cx="200" cy="200" r="125" fill="none" stroke="#3eb5a0" strokeWidth="1" opacity="0.4" strokeDasharray="4,4" />
        
        {/* White dots */}
        <circle cx="200" cy="75" r="5" fill="white" />
        <circle cx="200" cy="325" r="5" fill="white" />
      </svg>
    );
  }
};

const Services = () => {
  const [activeService, setActiveService] = useState(0);

  return (
    <section id="services" className="relative min-h-screen bg-[#2d2d2d]">
      <div className="grid lg:grid-cols-2 min-h-screen">
        {/* Left Side - Title and Arrow */}
        <div className="bg-[#2d2d2d] p-6 md:p-12 lg:p-16 flex flex-col justify-center">
          <div className="max-w-xl">
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#3eb5a0] mb-3">
              Services
            </h2>
            <p className="text-lg md:text-xl text-white/90 mb-8 md:mb-16 font-light">Three integrated strategic pillars</p>

            {/* Diagonal Arrow */}
            <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24">
              <MoveDownRight size={90} className="text-white/40 w-full h-full" strokeWidth={1} />
            </div>
          </div>
        </div>

        {/* Right Side - Service Cards */}
        <div className="bg-[#2d2d2d] p-6 md:p-12 lg:p-16 flex flex-col justify-center relative">
          <div className="space-y-0">
            {servicesData.map((service, index) => (
              <div
                key={service.id}
                className="group cursor-pointer transition-all duration-500 relative"
                onMouseEnter={() => setActiveService(index)}
                onClick={() => setActiveService(index)}
              >
                {/* Number positioned on right */}
                <div className="absolute top-4 md:top-8 right-0 text-6xl md:text-7xl lg:text-8xl font-bold text-[#3eb5a0]/30 group-hover:text-[#3eb5a0]/50 transition-colors duration-300">
                  {service.id}
                </div>

                <div className="border-t border-[#3eb5a0]/20 py-8 md:py-12 pr-20 md:pr-32">
                  <h3 className="text-2xl md:text-3xl font-semibold text-white mb-2 md:mb-3">
                    {service.title}
                  </h3>
                  {service.flagship && (
                    <p
                      data-testid={`service-${service.id}-flagship`}
                      className="text-sm md:text-base text-[#3eb5a0] font-medium tracking-wide mb-4 md:mb-5"
                    >
                      Flagship: {service.flagship}
                    </p>
                  )}

                  <p className="text-white/70 text-sm md:text-base leading-relaxed mb-6 md:mb-8 max-w-lg font-light">
                    {service.description}
                  </p>

                  {/* Graphic appears on hover or active on mobile */}
                  <div
                    className={`relative w-full transition-all duration-500 ${
                      activeService === index ? 'opacity-100 h-48 md:h-64' : 'opacity-0 h-0'
                    }`}
                  >
                    <div className="w-48 h-48 md:w-64 md:h-64">
                      <ServiceGraphic type={index + 1} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;